import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPackage,
  FiTrendingUp,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
  FiArrowRight,
  FiEdit2,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiLoader,
  FiPlus,
  FiGrid
} from 'react-icons/fi';
import FirebaseProductService from '../../../services/product.service';
import { auth } from '../../../firebase/config';
import Banner1 from "../../../assets/styles/Girl.png";
import Banner2 from "../../../assets/styles/delighted-young-woman-clad-vibrant-red-dress-is-captured-pointing-her-right-with-look_116547-116725.jpg";
import Banner3 from "../../../assets/styles/girl-having-high-hopes-wishing-enter-university-cross-fingers-good-luck-looking-upper-left-co_1258-142110.jpg";

// Slider data with your provided images
const slides = [
  {
    id: 1,
    title: "Boost Your Sales Today!",
    description: "List 5 new products and get featured on our homepage",
    image: Banner1,
    cta: "Add Products",
    link: "/seller/inventory/new"
  },
  {
    id: 2,
    title: "Limited Time Offer",
    description: "20% commission discount on first 10 orders this month",
    image: Banner2,
    cta: "Learn More",
    link: "/seller/promotions"
  },
  {
    id: 3,
    title: "New Analytics Dashboard",
    description: "Track your performance with enhanced insights",
    image: Banner3,
    cta: "Explore Analytics",
    link: "/seller/analytics"
  }
];

const StatusBadge = ({ status }) => {
  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return {
          bg: 'bg-green-50',
          text: 'text-green-700',
          border: 'border-green-200',
          icon: <FiCheckCircle className="w-3 h-3" />
        };
      case 'pending':
        return {
          bg: 'bg-yellow-50',
          text: 'text-yellow-700',
          border: 'border-yellow-200',
          icon: <FiClock className="w-3 h-3" />
        };
      case 'rejected':
        return {
          bg: 'bg-red-50',
          text: 'text-red-700',
          border: 'border-red-200',
          icon: <FiAlertCircle className="w-3 h-3" />
        };
      default:
        return {
          bg: 'bg-gray-50',
          text: 'text-gray-700',
          border: 'border-gray-200',
          icon: null
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border ${config.bg} ${config.border} ${config.text}`}>
      {config.icon}
      <span className="text-xs font-medium capitalize">{status?.toLowerCase() || 'Unknown'}</span>
    </div>
  );
};

const StatCard = ({ title, value, change, icon, color, loading = false }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    orange: 'from-orange-500 to-orange-600',
    purple: 'from-purple-500 to-purple-600'
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium mb-2">{title}</p>
          {loading ? (
            <div className="flex items-center space-x-2">
              <FiLoader className="w-5 h-5 animate-spin text-gray-400" />
              <p className="text-gray-400 text-lg">Loading...</p>
            </div>
          ) : (
            <>
              <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
              {change && (
                <div className="flex items-center gap-1">
                  <span className={`text-sm font-medium ${change.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                    {change}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
        <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]} text-white`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

const ProductCard = ({ product, onView, onEdit }) => {
  // Get first image or placeholder
  const getProductImage = () => {
    if (product.images && product.images.length > 0) {
      const firstImage = product.images[0];
      return firstImage.data || firstImage.url || `https://via.placeholder.com/400x300/F3F4F6/6B7280?text=${encodeURIComponent(product.name.substring(0, 15))}`;
    }
    return `https://via.placeholder.com/400x300/F3F4F6/6B7280?text=${encodeURIComponent(product.name.substring(0, 15))}`;
  };

  // Format price
  const formatPrice = () => {
    if (product.price && product.currency) {
      if (product.currency === 'INR') {
        const formatted = new Intl.NumberFormat('en-IN', {
          maximumFractionDigits: 0
        }).format(product.price);
        return `₹${formatted}`;
      }
      return `${product.currency} ${new Intl.NumberFormat().format(product.price)}`;
    }
    return 'Price not set';
  };

  return (
    <div className="group bg-white rounded-xl border border-gray-100 hover:border-gray-200 transition-all duration-300 hover:shadow-lg overflow-hidden flex flex-col h-full">
      <div className="relative aspect-square overflow-hidden">
        <img
          src={getProductImage()}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 right-3">
          <StatusBadge status={product.status} />
        </div>
        <div className="absolute bottom-3 left-3">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-white/90 backdrop-blur-sm rounded-lg text-xs font-medium text-gray-700">
              {product.category || 'Uncategorized'}
            </span>
          </div>
        </div>
      </div>
      
      <div className="p-4 flex flex-col flex-grow">
        <h3 className="font-semibold text-gray-900 text-base mb-2 line-clamp-1">{product.name}</h3>
        
        <div className="mb-4 flex-grow">
          <p className="text-2xl font-bold text-gray-900 mb-1">{formatPrice()}</p>
          {product.quantity && (
            <p className="text-sm text-gray-500">
              Quantity: <span className="font-medium">{product.quantity}</span>
            </p>
          )}
        </div>
        
        <div className="flex gap-2 mt-auto">
          <button
            onClick={() => onView(product)}
            className="flex-1 py-2.5 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200 flex items-center justify-center gap-2"
          >
            <FiEye className="w-4 h-4" />
            View
          </button>
          <button
            onClick={() => onEdit(product)}
            className="flex-1 py-2.5 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <FiEdit2 className="w-4 h-4" />
            Edit
          </button>
        </div>
      </div>
    </div>
  );
};

const SellerDashboard = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [userProducts, setUserProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProducts: 0,
    approvedProducts: 0,
    pendingProducts: 0,
    totalViews: 0
  });
  const [user, setUser] = useState(null);

  // Fetch user data and products
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await fetchUserProducts(currentUser.uid);
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchUserProducts = async (userId) => {
    try {
      setLoading(true);
      const result = await FirebaseProductService.getUserProducts();
      
      if (result && result.products) {
        // Sort by creation date (newest first) and take top 5
        const sortedProducts = [...result.products]
          .sort((a, b) => {
            const dateA = a.createdAtTimestamp || (a.createdAt ? new Date(a.createdAt).getTime() : 0);
            const dateB = b.createdAtTimestamp || (b.createdAt ? new Date(b.createdAt).getTime() : 0);
            return dateB - dateA;
          })
          .slice(0, 5);
        
        setUserProducts(sortedProducts);
        
        // Calculate stats
        const statsData = {
          totalProducts: result.products.length,
          approvedProducts: result.products.filter(p => p.status === 'APPROVED').length,
          pendingProducts: result.products.filter(p => p.status === 'PENDING').length,
          totalViews: result.products.reduce((sum, p) => sum + (p.views || 0), 0)
        };
        
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-rotate slides
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleViewProduct = (product) => {
    navigate(`/seller/myproducts/${product.id}`);
  };

  const handleEditProduct = (product) => {
    navigate(`/seller/myproducts/${product.id}/edit`);
  };

  const handleAddProduct = () => {
    navigate('/seller/inventory/new');
  };

  const handleViewAllProducts = () => {
    navigate('/seller/myproducts');
  };

  const handleSliderAction = (link) => {
    navigate(link);
  };

  // Format large numbers
  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-600 mt-1">
                {user?.displayName ? `Welcome back, ${user.displayName}` : 'Welcome back'}! Here's your store overview.
              </p>
            </div>
            <button
              onClick={handleAddProduct}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-orange-600 text-white font-medium rounded-xl hover:bg-orange-700 transition-colors shadow-sm hover:shadow"
            >
              <FiPlus className="w-5 h-5" />
              Add Product
            </button>
          </div>

          {/* Stats Grid */}
          {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              title="Total Products"
              value={loading ? '...' : formatNumber(stats.totalProducts)}
              change={stats.totalProducts > 0 ? "+ Added" : null}
              icon={<FiGrid className="w-6 h-6" />}
              color="blue"
              loading={loading}
            />
            
            <StatCard
              title="Approved"
              value={loading ? '...' : formatNumber(stats.approvedProducts)}
              change={stats.approvedProducts > 0 ? "+ Live" : null}
              icon={<FiCheckCircle className="w-6 h-6" />}
              color="green"
              loading={loading}
            />
            
            <StatCard
              title="Pending"
              value={loading ? '...' : formatNumber(stats.pendingProducts)}
              change={stats.pendingProducts > 0 ? "Under review" : null}
              icon={<FiClock className="w-6 h-6" />}
              color="orange"
              loading={loading}
            />
            
            <StatCard
              title="Total Views"
              value={loading ? '...' : formatNumber(stats.totalViews)}
              change={stats.totalViews > 0 ? "+ Views" : null}
              icon={<FiEye className="w-6 h-6" />}
              color="purple"
              loading={loading}
            />
          </div> */}
        </div>

        {/* Image Slider - Updated with your banners */}
        <div className="relative rounded-2xl overflow-hidden shadow-lg mb-8">
          <div className="relative h-64 md:h-72">
            {slides.map((slide, index) => (
              <div
                key={slide.id}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${slide.image})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
                
                <div className="relative h-full flex items-center">
                  <div className="container mx-auto px-6 md:px-12">
                    <div className="max-w-lg">
                      <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                        {slide.title}
                      </h2>
                      <p className="text-white/90 text-lg mb-6">
                        {slide.description}
                      </p>
                      <button
                        onClick={() => handleSliderAction(slide.link)}
                        className="inline-flex items-center bg-orange-600 text-white font-semibold px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors"
                      >
                        {slide.cta}
                        <FiArrowRight className="ml-2 h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Slider Controls */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors backdrop-blur-sm"
          >
            <FiChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors backdrop-blur-sm"
          >
            <FiChevronRight className="h-5 w-5" />
          </button>
          
          {/* Slide Indicators */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentSlide ? 'bg-white w-8' : 'bg-white/50 w-2'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Products Section */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="p-6 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Recent Products</h2>
                <p className="text-gray-600 text-sm mt-1">Your latest 5 products</p>
              </div>
              
              <div className="flex items-center gap-4">
                {userProducts.length > 0 && (
                  <div className="text-sm text-gray-500">
                    Showing <span className="font-medium">{userProducts.length}</span> of {stats.totalProducts}
                  </div>
                )}
                <button
                  onClick={handleViewAllProducts}
                  className="inline-flex items-center gap-2 text-sm font-medium text-orange-600 hover:text-orange-700"
                >
                  View all products
                  <FiArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <FiLoader className="w-8 h-8 animate-spin text-gray-400 mb-3" />
                <p className="text-gray-500">Loading products...</p>
              </div>
            ) : userProducts.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiPackage className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No products yet</h3>
                <p className="text-gray-600 mb-6 max-w-sm mx-auto">
                  Start adding products to showcase them on the marketplace.
                </p>
                <button
                  onClick={handleAddProduct}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors"
                >
                  <FiPlus className="w-4 h-4" />
                  Add Your First Product
                </button>
              </div>
            ) : (
              <>
                {/* Mobile Layout - Single Column */}
                <div className="lg:hidden space-y-4">
                  {userProducts.map((product) => (
                    <div key={product.id} className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-start gap-4">
                        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-200">
                          <img
                            src={product.images?.[0]?.data || `https://via.placeholder.com/80/6B7280/F3F4F6?text=${encodeURIComponent(product.name.substring(0, 1))}`}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="font-medium text-gray-900 truncate">{product.name}</h3>
                            <StatusBadge status={product.status} />
                          </div>
                          <p className="text-sm text-gray-500 mb-2">{product.category || 'Uncategorized'}</p>
                          <div className="flex items-center justify-between mb-3">
                            <span className="font-bold text-gray-900">
                              {product.price ? `₹${new Intl.NumberFormat('en-IN').format(product.price)}` : 'Price not set'}
                            </span>
                            <span className="text-sm text-gray-500">
                              Qty: {product.quantity || 0}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleViewProduct(product)}
                              className="flex-1 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg flex items-center justify-center gap-2"
                            >
                              <FiEye className="w-4 h-4" />
                              View
                            </button>
                            <button
                              onClick={() => handleEditProduct(product)}
                              className="flex-1 py-2.5 text-sm font-medium text-white bg-orange-600 rounded-lg flex items-center justify-center gap-2"
                            >
                              <FiEdit2 className="w-4 h-4" />
                              Edit
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop Layout - Grid */}
                <div className="hidden lg:grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                  {userProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onView={handleViewProduct}
                      onEdit={handleEditProduct}
                    />
                  ))}
                </div>

                {/* View All CTA */}
                {userProducts.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-gray-100">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <p className="text-gray-700 font-medium">Manage all your products</p>
                        <p className="text-sm text-gray-500 mt-1">
                          View, edit, and organize your complete product catalog
                        </p>
                      </div>
                      <button
                        onClick={handleViewAllProducts}
                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors"
                      >
                        <FiGrid className="w-4 h-4" />
                        Go to Product Management
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerDashboard;